import { DefaultMap } from '@seaber/common/lib/collections';
import { toM3 } from '../../constants/VolumeUnits';
import {
  getMassUnitName,
  getVolumeUnitName,
} from '../../constants/capacityUnits';
import type { ShipId } from '../../graphql';
import {
  VolumeUnits,
  type VesselSpeedType,
  type VesselArea,
  type VesselSpeedConsumptionFragment,
  type VesselDraftDatapointFragment,
  type VesselSpeedTag,
  type ScenarioTimeline_ShipFragment,
  WeightUnits,
} from '../../graphql';
import { getShipWeightCapacity } from './Core';
import { omit } from 'lodash';
import { memoize } from '@seaber/common/lib/memoize';
import type { MassVolumeSize } from './Capacity';
import type { CubicMeters, MetricTonnes } from '../../constants/cargoUnits';
import type { OrgDefaultBunkerGradeIdsByArea } from '../../lib/org-area-bunker-grade.utils';

export type VSCSByTypeAndArea = Map<
  VesselSpeedType,
  Map<VesselArea | null, VesselSpeedConsumptionFragment[]>
>;

export type VSCSByTagTypeAndArea = Map<
  VesselSpeedTag | null,
  VSCSByTypeAndArea
>;

export type Vessel = ScenarioTimeline_ShipFragment & {
  speedConsumptions: VSCSByTypeAndArea;
  speedConsumptionsByTag: VSCSByTagTypeAndArea;
  draftDatapoints: VesselDraftDatapointFragment[];
  /**
   * Per-vessel ECA bunker grade override, parsed from
   * vesselBunkerGradeMappingsByShipId. When set for an area, the calc reprices
   * that area's sea-leg grade to it instead of the org-area default.
   */
  bunkerGradeIdsByArea?: OrgDefaultBunkerGradeIdsByArea;
};
export const defaultVessel: Vessel = {
  vesselAttributesAssociationsByShipId: { nodes: [] },
  vesselSpeedConsumptionsByShipId: { nodes: [] },
  vesselBunkerGradeMappingsByShipId: { nodes: [] },
  cargoCompartmentsByShipId: { nodes: [] },
  id: NaN as ShipId,
  volumeCapacity: 100000,
  volumeUnit: VolumeUnits.M_3,
  // No deck stowage by default. Already the effective value — the resolver reads an unset field as
  // absent — but stated so a strict test mock can read the field the capacity rule looks at.
  deckVolumeCapacity: null,
  weightCapacity: 100000,
  massCapacity: 100000,
  massUnit: WeightUnits.TONNE,
  inactive: false,
  externalFleet: false,
  speedConsumptions: new DefaultMap(() => new DefaultMap(() => [])),
  speedConsumptionsByTag: new Map(),
  shipVoyageNamingRuleRelationsByShipId: { nodes: [] },
  tcContractsByVesselId: { nodes: [] },
  vesselDraftDatapointsByShipId: { nodes: [] },
  draftDatapoints: [],
  vesselFulfillsRequirementAssociationsByVesselId: { nodes: [] },
  enableConsecutiveCoaVoyages: false,
};

export type NormalizedVessel = Omit<
  Vessel,
  'massCapacity' | 'volumeCapacity' | 'massUnit' | 'volumeUnit'
> & {
  capacity: MassVolumeSize;
};

export const normalizeVessel = memoize((vessel: Vessel): NormalizedVessel => {
  const { massCapacity, volumeCapacity } = getVesselCapacities(vessel);
  return {
    ...omit(vessel, [
      'massCapacity',
      'volumeCapacity',
      'massUnit',
      'volumeUnit',
      'weightCapacityType',
    ]),
    capacity: {
      // A dimension the vessel does not define caps nothing for the consumers of this shape.
      mass: massCapacity ?? Infinity,
      volume: volumeCapacity ?? Infinity,
    },
  };
});

/**
 * Returns vessel capacities in metric tonnes and cubic meters. A dimension the vessel does not
 * define is null — unknown, never zero — so that nothing downstream reads it as a limit of nothing.
 */
export function getVesselCapacities(
  vessel: ScenarioTimeline_ShipFragment | null,
): {
  massCapacity: MetricTonnes | null;
  massCapacitySource: 'DWCC' | 'DWT' | null;
  volumeCapacity: CubicMeters | null;
  deckVolumeCapacity: CubicMeters | null;
} {
  const massCapacity: MetricTonnes | null = vessel?.massCapacity
    ? getShipWeightCapacity(
        +vessel.massCapacity,
        vessel.massUnit ?? WeightUnits.TONNE,
      )
    : vessel?.weightCapacity
      ? (vessel.weightCapacity as MetricTonnes)
      : null;

  const massCapacitySource = vessel?.massCapacity
    ? 'DWCC'
    : vessel?.weightCapacity
      ? 'DWT'
      : null;

  const volumeCapacity =
    vessel?.volumeCapacity && vessel.volumeUnit
      ? toM3(+vessel.volumeCapacity, vessel.volumeUnit)
      : null;

  const deckVolumeCapacity =
    vessel?.deckVolumeCapacity && vessel.volumeUnit
      ? toM3(+vessel.deckVolumeCapacity, vessel.volumeUnit)
      : null;

  return {
    massCapacity,
    massCapacitySource,
    volumeCapacity,
    deckVolumeCapacity,
  };
}

/**
 * Whether the vessel's carrying limit in mass is unknown. Automatic planning excludes such a
 * vessel, because a plan it generates would rest on a limit nobody has entered. Reads the same
 * fields as the vessel data warning, so a planner sees the warning on exactly the vessels the
 * automatic surfaces leave out.
 */
export function isMassCapacityAbsent(
  vessel: ScenarioTimeline_ShipFragment | null,
): boolean {
  return getVesselCapacities(vessel).massCapacity === null;
}

/**
 * Whether automatic planning may build a plan around this vessel. Every surface that produces a
 * plan the planner did not place by hand — voyage suggestions, scenario improvements, the external
 * solver runs — reads this one test, so the surfaces cannot come to disagree about which vessels
 * they cover.
 *
 * spec: specs/constraints/automatic-planning-mass-capacity.md
 */
export function isEligibleForAutomaticPlanning(
  vessel: ScenarioTimeline_ShipFragment | null,
): boolean {
  return (
    vessel !== null &&
    !vessel.externalFleet &&
    !vessel.inactive &&
    !isMassCapacityAbsent(vessel)
  );
}

export type VesselDetailKey =
  | 'capacity'
  | 'draft'
  | 'loa'
  | 'breadth'
  | 'grossTonnage'
  | 'deckVolume';

export type VesselDetailDisplay = {
  key: VesselDetailKey;
  label: string;
  value: number;
  unit?: string;
};

/**
 * Ordered vessel details for the timeline row header: one entry per enabled
 * toggle whose value is present. Capacity expands to a mass entry then a volume
 * entry, each shown in the vessel's configured unit (mass falls back to DWT in
 * metric tonnes when DWCC is unset, matching the prior capacity label).
 */
export function getVesselDetailDisplays(
  vessel: ScenarioTimeline_ShipFragment | null,
  enabled: Record<VesselDetailKey, boolean>,
): VesselDetailDisplay[] {
  if (!vessel) return [];

  const displays: VesselDetailDisplay[] = [];
  // Fall back to the canonical unit (the calculation unit) when a vessel has no
  // configured mass/volume unit, so a figure is never shown without a unit.
  const massUnit = getMassUnitName(
    vessel.massUnit ?? WeightUnits.TONNE,
  )?.symbol;
  const volumeUnit = getVolumeUnitName(
    vessel.volumeUnit ?? VolumeUnits.M_3,
  )?.symbol;

  if (enabled.capacity) {
    if (vessel.massCapacity != null && +vessel.massCapacity > 0) {
      displays.push({
        key: 'capacity',
        label: '',
        value: +vessel.massCapacity,
        unit: massUnit,
      });
    } else if (vessel.weightCapacity != null && +vessel.weightCapacity > 0) {
      displays.push({
        key: 'capacity',
        label: 'DWT',
        value: +vessel.weightCapacity,
        unit: getMassUnitName(WeightUnits.TONNE)?.symbol,
      });
    }
    if (vessel.volumeCapacity != null && +vessel.volumeCapacity > 0) {
      displays.push({
        key: 'capacity',
        label: '',
        value: +vessel.volumeCapacity,
        unit: volumeUnit,
      });
    }
  }

  const addLength = (
    key: VesselDetailKey,
    label: string,
    raw: string | number | null | undefined,
  ) => {
    if (enabled[key] && raw != null && +raw > 0) {
      displays.push({ key, label, value: +raw, unit: 'm' });
    }
  };
  addLength('draft', 'Draft', vessel.draft);
  addLength('loa', 'LOA', vessel.loa);
  addLength('breadth', 'Breadth', vessel.breadth);

  if (
    enabled.grossTonnage &&
    vessel.grossTonnage != null &&
    +vessel.grossTonnage > 0
  ) {
    displays.push({
      key: 'grossTonnage',
      label: 'GT',
      value: +vessel.grossTonnage,
    });
  }

  if (
    enabled.deckVolume &&
    vessel.deckVolumeCapacity != null &&
    +vessel.deckVolumeCapacity > 0
  ) {
    displays.push({
      key: 'deckVolume',
      label: 'Deck',
      value: +vessel.deckVolumeCapacity,
      unit: volumeUnit,
    });
  }

  return displays;
}

/** Get set of channel names that are banned for a vessel based on channel incompatibilities */
export function getVesselBannedChannels(vessel: Vessel | null): Set<string> {
  const bannedChannels = new Set<string>();

  if (!vessel) {
    return bannedChannels;
  }

  vessel.vesselAttributesAssociationsByShipId?.nodes?.forEach((assoc) => {
    assoc?.vesselAttributeByVesselAttributeId?.channelIncompatibilitiesByVesselAttributeId?.nodes?.forEach(
      (n) => {
        if (n?.channelName) {
          bannedChannels.add(n.channelName.toLowerCase());
        }
      },
    );
  });

  return bannedChannels;
}
